<template>
  <organism-node-management
    :nodeList="nodeList"
    :tagsList="tagOptions"
    :snmpList="snmpOptions"
    :sshList="sshOptions"
    :selectedNodeDetail="selectedNodeDetail"
    :configChangeTrendDataSet="configChangeTrendDataSet"
    :configHistoryList="configHistoryList"
    :configHistoryCurrentPage="configHistoryCurrentPage"
    :totalConfigHistory="totalConfigHistory"
    :selectedConfigData="selectedConfigData"
    @onSelectedNode="onSelectedNode"
    @onUpdateSelectedDuration="onUpdateSelectedDuration"
    @onClickPagination="onClickPagination"
    @onClickCompareConfiguration="onClickCompareConfiguration"
    @onUpdateCurrentNodeTagList="onUpdateCurrentNodeTagList"
    @onDeleteNodeFromTripleDot="onDeleteNodeFromTripleDot"
    @onUpdateTagsFromTripleDot="onUpdateTagsFromTripleDot"
    @onUpdateSnmpFromTripleDot="onUpdateSnmpFromTripleDot"
    @onUpdateSshFromTripleDot="onUpdateSshFromTripleDot"
    @onSelectedConfigData="onSelectedConfigData"
  ></organism-node-management>
</template>

<script>
import OrganismNodeManagement from "@/components/organisms/networkConfigurationManagement/OrganismNodeManagement.vue"
import {
  getNodeList,
  getTagsList,
  getCredentialsList,
  getNodeDetailById,
  getConfigChangeTrendGraph,
  getConfigurationChangeList,
  getNodeConfigurationDetail,
  updateNodeTag,
  updateNodesTags,
  updateNodesSnmp,
  updateNodesRemote,
  deleteNodes,
} from "@/api/api-module4"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
import { DateTime } from "luxon"
export default {
  name: "NodeManagement",
  components: {
    OrganismNodeManagement,
  },
  mixins: [mixins, mixinsValidator],
  data() {
    return {
      nodeList: [],
      tagOptions: [],
      snmpOptions: [],
      sshOptions: [],
      selectedNodeDetail: null,
      configChangeTrendDataSet: [],
      selectedConfigChangeDuration: {
        name: "30 Days",
        value: 30,
      },
      configHistoryList: [],
      configHistoryCurrentPage: 1,
      totalConfigHistory: 0,
      selectedConfigData: "",
    }
  },
  computed: {
    selectedNodeId() {
      return this.$route?.params?.node_id
    },
  },
  watch: {
    selectedNodeId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getNodeDetailByIdFromApi()
      }
    },
  },
  mounted() {
    this.fetchDataFromApi()
  },
  methods: {
    async fetchDataFromApi() {
      await this.getNodeListFromApi()
      await this.getNodeTagListFromApi()
      await this.getSnmpCommunityListFromApi()
      await this.getSshCommunityListFromApi()
      await this.selectNodeWithCondition()
    },

    async getNodeListFromApi() {
      this.showLoading(true)
      const filter = {
        PageSize: 100,
      }
      await getNodeList(filter)
        .then((response) => {
          if (response) {
            this.nodeList = response.data.map((node) => {
              return {
                nodeId: node.nodeId,
                hostname: node.hostname,
                description: node.description,
                ipAddress: node.ipAddress,
                nodeStatus: node.nodeStatus,
                backupStatus: node.backupStatus,
                tags: node.tags,
                isChecked: false,
                isSelected: false,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getNodeTagListFromApi() {
      this.showLoading(true)
      await getTagsList()
        .then((response) => {
          if (response) {
            this.tagOptions = response
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getSnmpCommunityListFromApi() {
      this.showLoading(true)
      const filter = {
        PageSize: 100,
      }
      await getCredentialsList("snmp", filter)
        .then((response) => {
          if (response) {
            this.snmpOptions = response.data
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getSshCommunityListFromApi() {
      this.showLoading(true)
      const filter = {
        PageSize: 100,
      }
      await getCredentialsList("remote", filter)
        .then((response) => {
          if (response) {
            this.sshOptions = response.data
          }
        })
        .finally(() => this.showLoading(false))
    },

    async selectNodeWithCondition() {
      let selectedNodeId = this.selectedNodeId
      let isSelectedIdInNodeList = this.nodeList.find(
        (node) => node.nodeId === selectedNodeId
      )
      if (selectedNodeId && isSelectedIdInNodeList) {
        await this.getNodeDetailByIdFromApi()
      } else if (
        this.nodeList.length &&
        selectedNodeId !== this.nodeList[0].nodeId
      ) {
        this.$router.push(
          `/network-configuration/node-management/${this.nodeList[0].nodeId}`
        )
      } else if (selectedNodeId) {
        this.$router.push("/network-configuration/node-management")
      }
    },

    async getConfigChangeTrendGraphFromApi() {
      this.showLoading(true)
      this.configChangeTrendDataSet = []
      let selectedNodeId = this.selectedNodeId
      let duration = this.selectedConfigChangeDuration.value
      await getConfigChangeTrendGraph(selectedNodeId, duration)
        .then((response) => {
          if (response) {
            let graphDataSetTimestamp = response.data.dateTime
            let graphDataSetValue = response.data.count
            // Pair the value from backend to use in echart
            graphDataSetTimestamp?.forEach((timestamp, i) => {
              graphDataSetValue.forEach((value, j) => {
                if (i === j) {
                  this.configChangeTrendDataSet.push({
                    timestamp: timestamp,
                    value: value,
                  })
                }
              })
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getConfigChangeListFromApi() {
      this.showLoading(true)
      const nodeConfigChangeFilter = {
        NodeId: this.selectedNodeId,
        PageNumber: this.configHistoryCurrentPage,
        PageSize: 5,
      }
      await getConfigurationChangeList(nodeConfigChangeFilter)
        .then((response) => {
          this.totalConfigHistory = response.totalCount
          this.configHistoryList = response.data.map((history) => {
            return {
              nodeId: history.nodeId,
              version: history.version,
              timestamp: history.timestamp,
              date: DateTime.fromISO(history.timestamp).toFormat("dd/MM/yyyy"),
              time: DateTime.fromISO(history.timestamp).toFormat("HH:mm:ss"),
              ipAddress: history.ipAddress,
              nodeName: history.hostname,
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getNodeDetailByIdFromApi() {
      this.showLoading(true)
      await getNodeDetailById(this.selectedNodeId)
        .then(async (response) => {
          if (response) {
            this.selectedNodeDetail = response.data
            this.nodeList.forEach((node) => {
              node.isSelected = false
            })
            let targetSelectedNodeIndex = this.nodeList.findIndex(
              (node) => node.nodeId === this.selectedNodeDetail.nodeId
            )
            this.nodeList[targetSelectedNodeIndex].isSelected = true
            await this.getConfigChangeTrendGraphFromApi()
            await this.getConfigChangeListFromApi()
          }
        })
        .finally(() => this.showLoading(false))
    },

    async sendRequestUpdateNodeTagToApi(tagList) {
      this.showLoading(true)
      await updateNodeTag(this.selectedNodeId, tagList)
        .then((response) => {
          if (response) {
            this.toastSuccess("Updated Tags Successfully")
            this.fetchDataFromApi()
          } else {
            this.toastError("Update Tags Failed")
          }
        })
        .finally(() => this.showLoading(false))
    },

    async sendRequestUpdateNodesTagsToApi(editTagRequest) {
      this.showLoading(true)
      await updateNodesTags(editTagRequest)
        .then((response) => {
          if (response) {
            this.toastSuccess("Updated Tags Successfully")
            this.fetchDataFromApi()
          } else {
            this.toastError("Update Tags Failed")
          }
        })
        .finally(() => this.showLoading(false))
    },

    async sendRequestUpdateNodesSnmpToApi(editSnmpRequest) {
      this.showLoading(true)
      await updateNodesSnmp(editSnmpRequest)
        .then((response) => {
          if (response) {
            this.toastSuccess("Updated Credential Successfully")
            this.fetchDataFromApi()
          } else {
            this.toastError("Update Credential Failed")
          }
        })
        .finally(() => this.showLoading(false))
    },

    async sendRequestUpdateNodesRemoteToApi(editSshRequest) {
      this.showLoading(true)
      await updateNodesRemote(editSshRequest)
        .then((response) => {
          if (response) {
            this.toastSuccess("Updated Credential Successfully")
            this.fetchDataFromApi()
          } else {
            this.toastError("Update Credential Failed")
          }
        })
        .finally(() => this.showLoading(false))
    },

    async sendRequestDeleteNodesToApi(deleteNodeRequest) {
      this.showLoading(true)
      await deleteNodes(deleteNodeRequest)
        .then((response) => {
          if (response) {
            this.toastSuccess("Deleted Successfully")
            this.$router.go()
          } else {
            this.toastError("Delete Failed")
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getConfigurationDataFromApi(nodeId, version) {
      this.showLoading(true)
      await getNodeConfigurationDetail(nodeId, version)
        .then((response) => {
          if (response) {
            this.selectedConfigData = response.data.configuration
          }
        })
        .finally(() => this.showLoading(false))
    },

    onSelectedNode(nodeId) {
      if (nodeId !== this.selectedNodeId) {
        const path = "/network-configuration/node-management/" + nodeId
        this.$router.push(path)
      }
    },

    onUpdateCurrentNodeTagList(tagList) {
      this.sendRequestUpdateNodeTagToApi(tagList)
    },

    onDeleteNodeFromTripleDot(deleteNodeRequest) {
      this.sendRequestDeleteNodesToApi(deleteNodeRequest)
    },

    onUpdateTagsFromTripleDot(editTagRequest) {
      this.sendRequestUpdateNodesTagsToApi(editTagRequest)
    },

    onUpdateSnmpFromTripleDot(editSnmpRequest) {
      this.sendRequestUpdateNodesSnmpToApi(editSnmpRequest)
    },

    onUpdateSshFromTripleDot(editSshRequest) {
      this.sendRequestUpdateNodesRemoteToApi(editSshRequest)
    },

    onUpdateSelectedDuration(selectedDuration) {
      this.selectedConfigChangeDuration = selectedDuration
      this.getConfigChangeTrendGraphFromApi()
    },

    onClickPagination(currentPage) {
      this.configHistoryCurrentPage = currentPage
      this.getConfigChangeListFromApi()
    },
    onClickCompareConfiguration(selectedNodeId, selectedConfigVersion) {
      if (selectedConfigVersion !== 1) {
        this.$router.replace({
          path: "/network-configuration/configuration-comparison",
          query: {
            BaselineNodeId: selectedNodeId,
            BaselineVersion: selectedConfigVersion - 1,
            CompareToNodeId: selectedNodeId,
            CompareToVersion: selectedConfigVersion,
          },
        })
      } else {
        this.$router.replace({
          path: "/network-configuration/configuration-comparison",
          query: {
            BaselineNodeId: selectedNodeId,
            BaselineVersion: selectedConfigVersion,
          },
        })
      }
    },
    onSelectedConfigData(nodeId, version) {
      this.getConfigurationDataFromApi(nodeId, version)
    },
  },
}
</script>

<style lang="scss" scoped></style>
